// The results rail of the /transcripts/$id canvas (docs/09a): one row per
// current Mark — Judged (rule / judge markers, verdict-ordered) then Measured
// (built-ins) — plus one row per human-only marker with a saved Label, and
// exactly one expanded (`?label=<answerId>`). The expanded row is where a
// human Label is written — Agree / Disagree / Correct… / Flag, then Save —
// through the `LabelEditor` owned by useLabelInspector. The Add-a-question
// form (`?label=new`) renders at the top of the rail, never in a layer, so
// the conversation stays interactive for evidence picks while it is open.
// Keyboard shortcuts live here and are always listed in the footer.

import { useState, type ReactNode } from "react";

import type { components } from "../../api/schema";
import { eligibilityReasonMessage } from "../../lib/answerStatus";
import { persistMeasuredOpen, readMeasuredOpen } from "../../lib/preferences";
import { formatClock, formatJsonValue } from "../../lib/format";
import type { TranscriptTimelinePanel } from "../../lib/routerSearch";
import {
  Badge,
  Button,
  EmptyState,
  Inline,
  InlineError,
  Markdown,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { AnswerControl } from "./labelInspectorControls";
import {
  assertionSuggestions,
  assertionVerdict,
  booleanForVerdict,
  draftFromAnswer,
  flaggedReasonLabel,
  locatorLabel,
  nextFlaggedReason,
  wholeTranscriptLocator,
} from "./labelInspectorDraft";
import {
  labelVerdict,
  latestPerAuthor,
  resultsRailGlyph,
  resultsRailSummary,
  verdictWord,
  type ResultsRailRow,
} from "./resultsRailRows";
import { useLabelShortcuts } from "./useLabelShortcuts";
import type { LabelEditor } from "./useLabelInspector";

type UserResponse = components["schemas"]["UserResponse"];

export interface ResultsRailProps {
  editor: LabelEditor | null;
  errorMessage?: string | null;
  expandedId?: string;
  feed: boolean;
  hasAudio?: boolean;
  hasNotes: boolean;
  hasVariables: boolean;
  isPending?: boolean;
  onAddQuestion?: () => void;
  onBack: () => void;
  onCloseQuestion?: () => void;
  onExpand: (answerId: string) => void;
  onOpenPanel: (panel: TranscriptTimelinePanel) => void;
  onOpenTraceSpan?: (nodeId: string) => void;
  onReevaluate?: (markerId: string) => void;
  onSeek: (startMs: number) => void;
  // The Add-a-question form while `?label=new` is open.
  question?: ReactNode;
  readOnly?: boolean;
  rows: ResultsRailRow[];
  usersById?: ReadonlyMap<string, UserResponse>;
  viewerId?: string;
}

// The two boolean answers an asserted boolean Mark offers: agree lands on the
// Mark's verdict, disagree on the other boolean's verdict.
function booleanChoices(row: ResultsRailRow, editor: LabelEditor) {
  if (editor.output !== "boolean" || row.verdict == null) {
    return null;
  }
  const agree = booleanForVerdict(row.assertion, row.verdict);
  if (agree == null) {
    return null;
  }
  const disagreeVerdict = assertionVerdict(row.assertion, !agree);
  return {
    agree,
    agreeVerdict: row.verdict,
    disagree: !agree,
    disagreeVerdict,
  };
}

export function ResultsRail({
  editor,
  errorMessage,
  expandedId,
  feed,
  hasAudio = false,
  hasNotes,
  hasVariables,
  isPending = false,
  onAddQuestion,
  onBack,
  onCloseQuestion,
  onExpand,
  onOpenPanel,
  onOpenTraceSpan,
  onReevaluate,
  onSeek,
  question,
  readOnly = false,
  rows,
  usersById,
  viewerId,
}: ResultsRailProps) {
  const expanded = rows.find((row) => row.answer.id === expandedId);
  const summary = resultsRailSummary(rows);
  const canAnswer =
    editor != null &&
    expanded != null &&
    !expanded.builtIn &&
    !readOnly &&
    !editor.isLoading &&
    editor.loadError == null &&
    !editor.isSaving;
  const choices = editor && expanded ? booleanChoices(expanded, editor) : null;
  const judged = rows.filter((row) => !row.builtIn);
  const measured = rows.filter((row) => row.builtIn);

  useLabelShortcuts({
    enabled: canAnswer,
    feed,
    onAgree:
      editor && expanded && expanded.answer.value != null && !expanded.humanOnly
        ? () => {
            editor.onDraftChange({
              value: choices
                ? choices.agree
                : draftFromAnswer(expanded.answer).value,
            });
          }
        : undefined,
    // Esc closes an open question form before it leaves the transcript.
    onBack: question != null && onCloseQuestion ? onCloseQuestion : onBack,
    onDisagree:
      editor && choices
        ? () => {
            editor.onDraftChange({ value: choices.disagree });
          }
        : undefined,
    onFlag: editor
      ? () => {
          editor.onDraftChange({
            flaggedReason: nextFlaggedReason(editor.draft.flaggedReason),
          });
        }
      : undefined,
    onNext: editor?.onNext,
    onPrevious: editor?.onPrevious,
    onSaveNext: editor?.onSaveNext,
  });

  const renderRow = (row: ResultsRailRow) => {
    const isExpanded = row.answer.id === expandedId;
    const glyph = resultsRailGlyph(row);
    const chip =
      row.humanOnly && !isExpanded ? (
        <Badge>Human</Badge>
      ) : row.humanReference && !isExpanded ? (
        <Badge
          tone={
            row.humanReference.kind === "split"
              ? "warning"
              : row.humanReference.kind === "agreed"
                ? "success"
                : "danger"
          }
        >
          {row.humanReference.label}
        </Badge>
      ) : !row.humanReference && !row.builtIn && row.answer.needs_review ? (
        <Badge>Needs review</Badge>
      ) : null;
    return (
      <li
        className={
          isExpanded
            ? "marker-results-rail__row marker-results-rail__row--expanded"
            : "marker-results-rail__row"
        }
        key={row.answer.id}
      >
        <button
          aria-expanded={isExpanded}
          className="marker-results-rail__summary"
          type="button"
          onClick={() => {
            if (!isExpanded) {
              onExpand(row.answer.id);
            }
          }}
        >
          <span
            aria-hidden="true"
            className={`marker-results-rail__glyph marker-results-rail__glyph--${glyph.tone}`}
          >
            {glyph.glyph}
          </span>
          <span className="marker-results-rail__name">{row.name}</span>
          <span
            className={`marker-results-rail__value marker-results-rail__value--${row.valueTone}`}
          >
            {row.valueText}
          </span>
          <span className="marker-results-rail__chip">{chip}</span>
        </button>
        {isExpanded ? (
          <ExpandedRow
            choices={choices}
            editor={editor}
            feed={feed}
            onOpenTraceSpan={onOpenTraceSpan}
            onReevaluate={onReevaluate}
            onSeek={onSeek}
            readOnly={readOnly}
            row={row}
            usersById={usersById}
          />
        ) : null}
      </li>
    );
  };

  return (
    <section aria-label="Results" className="marker-results-rail">
      <div className="marker-results-rail__head">
        <Text as="span" variant="caps">
          Results
        </Text>
        <Text as="span" variant="muted">
          {summary.needsReview > 0
            ? `${String(summary.needsReview)} to review · `
            : ""}
          {String(summary.failed)} failed · {String(summary.passed)} passed
        </Text>
      </div>
      {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
      {editor && !expanded && editor.loadError ? (
        <InlineError>{editor.loadError}</InlineError>
      ) : null}
      {question != null ? (
        <div className="marker-results-rail__question">{question}</div>
      ) : null}
      {isPending && rows.length === 0 ? (
        <Skeleton label="Loading results" rows={4} shape="table-row" />
      ) : null}
      {!isPending && !errorMessage && rows.length === 0 && question == null ? (
        <EmptyState title="No results" />
      ) : null}
      {judged.length > 0 ? (
        <ul aria-label="Judged" className="marker-results-rail__rows">
          <li className="marker-results-rail__group">Judged</li>
          {judged.map(renderRow)}
        </ul>
      ) : null}
      {measured.length > 0 ? (
        <MeasuredRows
          count={measured.length}
          key={viewerId}
          viewerId={viewerId}
        >
          {measured.map(renderRow)}
        </MeasuredRows>
      ) : null}
      <Inline className="marker-results-rail__secondary" gap={4}>
        <Button
          size="sm"
          type="button"
          variant="ghost"
          onClick={() => {
            onOpenPanel("traces");
          }}
        >
          Traces
        </Button>
        {hasVariables ? (
          <Button
            size="sm"
            type="button"
            variant="ghost"
            onClick={() => {
              onOpenPanel("variables");
            }}
          >
            Variables
          </Button>
        ) : null}
        {hasNotes ? (
          <Button
            size="sm"
            type="button"
            variant="ghost"
            onClick={() => {
              onOpenPanel("notes");
            }}
          >
            Notes
          </Button>
        ) : null}
        {onAddQuestion && !readOnly ? (
          <Button
            size="sm"
            type="button"
            variant="ghost"
            onClick={onAddQuestion}
          >
            Add a question
          </Button>
        ) : null}
      </Inline>
      <div
        aria-label="Keyboard shortcuts"
        className="marker-results-rail__shortcuts"
      >
        <Shortcut keys={["A"]}>agree</Shortcut>
        <Shortcut keys={["D"]}>disagree</Shortcut>
        <Shortcut keys={["F"]}>flag</Shortcut>
        <Shortcut keys={["↵"]}>save and next</Shortcut>
        {feed ? <Shortcut keys={["[", "]"]}>prev / next</Shortcut> : null}
        {hasAudio ? <Shortcut keys={["space"]}>play / pause</Shortcut> : null}
        <Shortcut keys={["esc"]}>{feed ? "back to review" : "back"}</Shortcut>
      </div>
    </section>
  );
}

function MeasuredRows({
  children,
  count,
  viewerId,
}: {
  children: ReactNode;
  count: number;
  viewerId?: string;
}) {
  const [open, setOpen] = useState(() => readMeasuredOpen(viewerId));
  return (
    <div>
      <Button
        aria-expanded={open}
        size="sm"
        type="button"
        variant="ghost"
        onClick={() => {
          setOpen(!open);
          persistMeasuredOpen(viewerId, !open);
        }}
      >
        Measured ({String(count)})
      </Button>
      {open ? (
        <ul aria-label="Measured" className="marker-results-rail__rows">
          {children}
        </ul>
      ) : null}
    </div>
  );
}

function Shortcut({ children, keys }: { children: ReactNode; keys: string[] }) {
  return (
    <Text as="span" className="marker-results-rail__shortcut" variant="muted">
      {keys.map((key) => (
        <kbd className="marker-results-rail__kbd" key={key}>
          {key}
        </kbd>
      ))}
      {children}
    </Text>
  );
}

function ExpandedRow({
  choices,
  editor,
  feed,
  onOpenTraceSpan,
  onReevaluate,
  onSeek,
  readOnly,
  row,
  usersById,
}: {
  choices: ReturnType<typeof booleanChoices>;
  editor: LabelEditor | null;
  feed: boolean;
  onOpenTraceSpan?: (nodeId: string) => void;
  onReevaluate?: (markerId: string) => void;
  onSeek: (startMs: number) => void;
  readOnly: boolean;
  row: ResultsRailRow;
  usersById?: ReadonlyMap<string, UserResponse>;
}) {
  const { answer } = row;
  const markDraftValue = draftFromAnswer(answer).value;
  // A human-only row has no Mark to agree with: its own answer opens editable.
  const [correcting, setCorrecting] = useState(row.humanOnly);
  const [justificationOpen, setJustificationOpen] = useState(false);
  const isCorrecting =
    correcting ||
    (editor != null &&
      choices == null &&
      editor.draft.value !== markDraftValue &&
      editor.draft.value !== null &&
      editor.draft.value !== "");
  const suggestions = editor
    ? [
        ...new Set([
          ...assertionSuggestions(row.assertion),
          ...row.labels.flatMap((label) =>
            typeof label.value === "string" ? [label.value] : [],
          ),
        ]),
      ]
    : [];
  const canAnswer =
    editor != null &&
    !row.builtIn &&
    !readOnly &&
    !editor.isLoading &&
    editor.loadError == null;
  const locatorPicked =
    editor != null &&
    JSON.stringify(editor.draft.locator) !==
      JSON.stringify(answer.locator ?? wholeTranscriptLocator);

  return (
    <Stack className="marker-results-rail__body" gap={2}>
      {answer.justification ? (
        <div>
          <div
            className={
              justificationOpen
                ? "marker-results-rail__justification"
                : "marker-results-rail__justification marker-fade-y"
            }
          >
            <Markdown>{`“${answer.justification.trim()}”`}</Markdown>
          </div>
          <button
            aria-expanded={justificationOpen}
            className="marker-results-rail__text-button"
            type="button"
            onClick={() => {
              setJustificationOpen((open) => !open);
            }}
          >
            {justificationOpen ? "less" : "more"}
          </button>
        </div>
      ) : null}
      {row.evidence ? (
        <Text as="span" variant="muted">
          Evidence {formatClock(row.evidence.startMs)} –{" "}
          {formatClock(row.evidence.endMs)} ·{" "}
          <button
            className="marker-results-rail__text-button"
            type="button"
            onClick={() => {
              onSeek(row.evidence?.startMs ?? 0);
            }}
          >
            play
          </button>
        </Text>
      ) : null}
      {answer.locator?.kind === "trace_node" && onOpenTraceSpan ? (
        <Text as="span" variant="muted">
          Evidence trace span ·{" "}
          <button
            className="marker-results-rail__text-button"
            type="button"
            onClick={() => {
              if (answer.locator?.kind === "trace_node") {
                onOpenTraceSpan(answer.locator.node_id);
              }
            }}
          >
            open trace span
          </button>
        </Text>
      ) : null}
      {answer.eval_status === "not_eligible" ? (
        <Text variant="muted">{eligibilityReasonMessage(answer)}</Text>
      ) : null}
      {row.builtIn ? (
        <Text variant="muted">
          Built-in markers are measured, not judged, and cannot be labeled.
        </Text>
      ) : null}
      {editor?.isLoading ? (
        <Skeleton label="Loading Label" shape="detail-field" />
      ) : null}
      {editor?.loadError ? <InlineError>{editor.loadError}</InlineError> : null}
      {editor && canAnswer ? (
        <Stack className="marker-results-rail__answer" gap={2}>
          <Inline
            className="marker-results-rail__actions"
            gap={2}
            justify="between"
          >
            <Inline className="marker-results-rail__actions-left" gap={2}>
              {choices ? (
                <>
                  <kbd className="marker-results-rail__kbd">A</kbd>
                  <Button
                    aria-pressed={editor.draft.value === choices.agree}
                    disabled={editor.isSaving}
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      editor.onDraftChange({ value: choices.agree });
                    }}
                  >
                    Agree, {verdictWord(choices.agreeVerdict)}
                  </Button>
                  <kbd className="marker-results-rail__kbd">D</kbd>
                  <Button
                    aria-pressed={editor.draft.value === choices.disagree}
                    disabled={editor.isSaving}
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      editor.onDraftChange({ value: choices.disagree });
                    }}
                  >
                    Disagree
                    {choices.disagreeVerdict
                      ? `, ${verdictWord(choices.disagreeVerdict)}`
                      : ""}
                  </Button>
                </>
              ) : (
                <>
                  {answer.value != null && !row.humanOnly ? (
                    <>
                      <kbd className="marker-results-rail__kbd">A</kbd>
                      <Button
                        aria-pressed={
                          !isCorrecting && editor.draft.value === markDraftValue
                        }
                        disabled={editor.isSaving}
                        size="sm"
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setCorrecting(false);
                          editor.onDraftChange({ value: markDraftValue });
                        }}
                      >
                        Agree
                      </Button>
                    </>
                  ) : null}
                  <Button
                    aria-pressed={isCorrecting}
                    disabled={editor.isSaving}
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setCorrecting(true);
                    }}
                  >
                    Correct…
                  </Button>
                </>
              )}
              <kbd className="marker-results-rail__kbd">F</kbd>
              <Button
                aria-pressed={editor.draft.flaggedReason != null}
                disabled={editor.isSaving}
                size="sm"
                type="button"
                variant="ghost"
                onClick={() => {
                  editor.onDraftChange({
                    flaggedReason: nextFlaggedReason(
                      editor.draft.flaggedReason,
                    ),
                  });
                }}
              >
                {editor.draft.flaggedReason
                  ? `Flagged: ${flaggedReasonLabel(editor.draft.flaggedReason)}`
                  : "Flag"}
              </Button>
            </Inline>
            <Button
              disabled={editor.isSaving}
              size="sm"
              type="button"
              onClick={
                editor.validationError ? editor.onRetry : editor.onSaveNext
              }
            >
              {editor.isSaving
                ? "Saving..."
                : editor.validationError
                  ? "Retry"
                  : feed
                    ? "Save and next"
                    : "Save"}
            </Button>
          </Inline>
          {choices == null && isCorrecting ? (
            <AnswerControl
              draft={editor.draft}
              output={editor.output}
              suggestionId={`${answer.id}-suggestions`}
              suggestions={suggestions}
              onChange={editor.onDraftChange}
            />
          ) : null}
          {locatorPicked ? (
            <Inline gap={2}>
              <Text as="span" variant="muted">
                Where: {locatorLabel(editor.draft.locator)}
              </Text>
              <Button
                size="sm"
                type="button"
                variant="ghost"
                onClick={() => {
                  editor.onDraftChange({
                    locator: answer.locator ?? wholeTranscriptLocator,
                  });
                }}
              >
                Reset
              </Button>
            </Inline>
          ) : null}
          {editor.validationError ? (
            <InlineError>{editor.validationError}</InlineError>
          ) : null}
        </Stack>
      ) : null}
      {editor?.incomingAnswer ? (
        <Text variant="muted">
          {editor.incomingAnswer.author_display_name ?? "Another reviewer"} just
          labeled this
        </Text>
      ) : null}
      {row.labels.length > 0 ? (
        <Text className="marker-fade-x" variant="muted">
          Previously:{" "}
          {latestPerAuthor(row.labels)
            .map((label) => {
              const author =
                label.author_display_name ??
                (label.author_id
                  ? usersById?.get(label.author_id)?.display_name
                  : undefined) ??
                "Someone";
              const verdict = labelVerdict(label, row.assertion);
              const value = verdict
                ? verdictWord(verdict)
                : formatJsonValue(label.value);
              const flag = label.flagged_reason
                ? ` (${flaggedReasonLabel(label.flagged_reason)})`
                : "";
              const edited = label.edited ? " (edited)" : "";
              return `${author} labeled ${value}${flag}${edited}`;
            })
            .join(" · ")}
        </Text>
      ) : null}
      {onReevaluate && !readOnly && !row.humanOnly ? (
        <Button
          size="sm"
          type="button"
          variant="ghost"
          onClick={() => {
            onReevaluate(answer.marker_id);
          }}
        >
          Re-evaluate this marker
        </Button>
      ) : null}
    </Stack>
  );
}
